import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import {
  useWindowContext,
  Page,
  noop,
  Status,
  Input,
  Select,
  Header,
  Row, Footer, Button, Col, Modal, Notification, Message,
} from '@iqueue/ui-kit'
import {
  BleClient,
  numbersToDataView,
  numberToUUID,
} from '@capacitor-community/bluetooth-le'
import {
  BLE_OTA_RECV_FW_UUID,
  BLE_OTA_CMD_UUI,
  BLE_OTA_START,
  BLE_OTA_STOP, PTA_SERVICE_UUID, BLE_FRAME,
} from './ble/constants'
import useTranslate, { t } from '../../hooks/useTranslate'
import ErrorView from '../../components/errors/ErrorView'
import { useBLE } from '../../hooks/useBLE'
import { assoc } from 'ramda'
import cn from 'classnames'
import Queue from '../../utils/Queue'
import {
  units,
  bleUnitToHuman,
  safeBleUnit,
  BLE_SET_UNIT,
  BLE_READ_UNIT,
  BLE_READ_STATE,
  BLE_READ_UPLINKS_COUNT,
  BLE_SET_COLD_START,
  BLE_READ_COLD_START,
  BLE_READ_DUTY_CYCLE,
  BLE_INFORMATION_SERVICE,
  AMBER_SERVICE,
  AMBER_TUNER_SERVICE,
  LAST_SEQUENCE,
  AMBER_TUNER_CHARACTERISTIC, BleUnit, BLE_SET_DUTY_CYCLE,
} from './ble/constants'

const SECTOR_SIZE = 4096
const PACKET_HEADER_SIZE = 3
const DEFAULT_PACKET_SIZE = 510
const RESPONSE_POLL_INTERVAL_MS = 50
const COMMAND_TIMEOUT_MS = 5000
const FW_TIMEOUT_MS = 5000

function errorMessage(reason) {
  Message({
    title: reason,
    type: 'error',
    timeout: 100000,
  })
}

function PickUnit({ value, onChange, ...props }) {
  const entries = useMemo(() => {
    return Object
      .keys(units)
      .map(v => v * 1)
      .sort((a, b) => a - b)
  }, [])

  const renderUnit = useCallback((unit) => {
    return {
      id: unit, title: <span>{ safeBleUnit(unit).description } <b>{ safeBleUnit(unit).literal }</b></span>,
    }
  }, [])

  return <Select
    { ...props }
    name={ 'unit' }
    value={ value }
    onChange={ onChange }
    entries={ entries }
    render={ renderUnit }
  />
}

function BleUnitCard(props) {
  const {
    data: {
      unit: deviceUnit,
      errorCode,
      minSensorValue,
      maxSensorValue,
      minWorkValue = 0,
      maxWorkValue = 0,
    },
    onApply,
  } = props

  const [minVal, setMinVal] = useState(minWorkValue.toFixed(5) * 1)
  const [maxVal, setMaxVal] = useState(maxWorkValue.toFixed(5) * 1)
  const [unit, setUnit] = useState(deviceUnit)

  const checkValidity = useCallback((value) => {
    return value >= minSensorValue && value <= maxSensorValue && minVal < maxVal
  }, [minSensorValue, maxSensorValue, minVal, maxVal])

  const isDataInvalid = useMemo(() => {
    return !checkValidity(minVal) || !checkValidity(maxVal)
  }, [checkValidity, minVal, maxVal])

  const handleApplyClick = useCallback(() => {
    const buffer = new ArrayBuffer(10)
    const view = new DataView(buffer)

    view.setUint8(0, BLE_SET_UNIT)
    view.setUint8(1, unit)
    view.setFloat32(2, minVal, false)
    view.setFloat32(6, maxVal, false)

    return onApply([
      { id: BLE_SET_UNIT, data: new Uint8Array(buffer) },
      { id: BLE_READ_UNIT },
    ])
  }, [unit, minVal, maxVal, checkValidity])

  useEffect(() => {
    setUnit(deviceUnit)
    setMinVal(minWorkValue.toFixed(5) * 1)
    setMaxVal(maxWorkValue.toFixed(5) * 1)
  }, [props.data])

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error' }>
      <Header
        title={ 'Единица измерения' }
      />

      <div className={ 'content' }>
        <b>Ошибка при получении данных ({ errorCode })</b>
      </div>

      <Footer>
        <Row justify={ 'end' }>
          <Button
            caution
            submit
            onClick={ () => onApply([{ id: BLE_READ_UNIT }]) }
          >Перезапросить</Button>
        </Row>
      </Footer>
    </div>
  }

  return <div className={ 'gas-and-oil-card' }>
    <Header
      title={ 'Единица измерения' }
      extra={ <Button
        flat
        icon={ 'sync' }
        style={ { marginRight: 'calc(var(--iq-grid-spacing) * -0.5)' } }
        onClick={ () => onApply([{ id: BLE_READ_UNIT }]) }
      /> }
    />

    <div className={ 'content' }>
      <div>{ safeBleUnit(unit).description } <b>{ safeBleUnit(unit).literal }</b></div>
      <div>
        <span>Минимальное значение: </span>
        <b>
          <BleUnit unit={ unit } value={ minSensorValue }/>
        </b>
      </div>
      <div>
        <span>Максимальное значение: </span>
        <b>
          <BleUnit unit={ unit } value={ maxSensorValue }/>
        </b>
      </div>

      <Row>
        <PickUnit
          size={ 12 }
          name={ 'unit' }
          value={ unit }
          onChange={ setUnit }
        />
      </Row>

      <Row>
        <Input
          size={ 12 }
          name={ 'minWorkValue' }
          type={ 'float' }
          placeholder={ 'Минимальное значение' }
          hintVisible
          checkValidity={ checkValidity }
          hint={ `${ bleUnitToHuman(unit, minSensorValue) } .. ${ bleUnitToHuman(unit, maxSensorValue) }` }
          value={ minVal }
          onChange={ setMinVal }
        />
      </Row>

      <Row>
        <Input
          size={ 12 }
          name={ 'maxWorkValue' }
          type={ 'float' }
          placeholder={ 'Максимальное значение' }
          hintVisible
          checkValidity={ checkValidity }
          hint={ `${ bleUnitToHuman(unit, minSensorValue) } .. ${ bleUnitToHuman(unit, maxSensorValue) }` }
          value={ maxVal }
          onChange={ setMaxVal }
        />
      </Row>
    </div>

    <Footer>
      <Row justify={ 'end' }>
        <Button
          caution
          submit
          onClick={ handleApplyClick }
          disabled={ isDataInvalid }
        >Записать на устройство</Button>
      </Row>
    </Footer>
  </div>
}

function BleCurrentValueCard(props) {
  const {
    data: {
      battery,
      errorCode,
      deviceTemperature,
      value,
    },
    onApply,
  } = props

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error full-width' }>
      <div className={ 'content' }>
        <p><b>Ошибка при получении данных ({ errorCode })</b></p>

        <Button
          submit
          style={ { margin: 0 } }
          onClick={ () => onApply([{ id: BLE_READ_STATE }]) }
          caution
        >Перезапросить</Button>
      </div>
    </div>
  }

  return <div
    className={ 'production-summary-cards-wrapper fill' }
    onClick={ () => onApply([{ id: BLE_READ_STATE }]) }
  >
    <div className={ 'cards' }>
      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <h1>{ battery }</h1>
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>Заряд батарейки</b>
          </div>
        </Footer>
      </div>

      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <h1>{ value.toFixed(2) }</h1>
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>Текущее значение</b>
          </div>
        </Footer>
      </div>

      <div className={ 'gas-and-oil-summary-card' }>
        <div className={ 'content' }>
          <h1>{ deviceTemperature }</h1>
        </div>

        <Footer>
          <div className={ 'iq-spacer' }>
            <b>Температура устройства</b>
          </div>
        </Footer>
      </div>
    </div>
  </div>
}

function BleBootCountCard(props) {
  const {
    data: {
      count,
      errorCode,
    },
    onApply,
  } = props

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error' }>
      <Header title={ 'Количество отправленных пакетов' }/>

      <div className={ 'content' }>
        <b>Ошибка при получении данных ({ errorCode })</b>
      </div>

      <Footer>
        <Row justify={ 'end' }>
          <Button
            caution
            submit
            onClick={ () => onApply([{ id: BLE_READ_UPLINKS_COUNT }]) }
          >Перезапросить</Button>
        </Row>
      </Footer>
    </div>
  }

  return <div className={ 'gas-and-oil-card stats' }>
    <Header
      title={ 'Количество отправленных пакетов' }
      extra={ <Button
        flat
        icon={ 'sync' }
        style={ { marginRight: 'calc(var(--iq-grid-spacing) * -0.5)' } }
        onClick={ () => onApply([{ id: BLE_READ_UPLINKS_COUNT }]) }
      /> }
    />

    <div className={ 'content' }>
      <h3>{ count }</h3>
    </div>
  </div>
}

function BleDutyCycleCard(props) {
  const {
    data: {
      dutyCycle,
      errorCode,
    },
    onApply,
  } = props

  const [value, setValue] = useState(dutyCycle)

  const handleApplyClick = useCallback(() => {
    const buffer = new ArrayBuffer(3)
    const view = new DataView(buffer)

    view.setUint8(0, BLE_SET_DUTY_CYCLE)
    view.setUint16(1, value, false)

    return onApply([
      { id: BLE_SET_DUTY_CYCLE, data: new Uint8Array(buffer) },
    ])
  }, [value])

  const checkValidity = useCallback((value) => {
    return value >= 0 && value <= 65535
  }, [])

  useEffect(() => {
    setValue(dutyCycle)
  }, [dutyCycle])

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error' }>
      <Header title={ 'Интервал выхода на связь' }/>

      <div className={ 'content' }>
        <b>Ошибка при получении данных ({ errorCode })</b>
      </div>

      <Footer>
        <Row justify={ 'end' }>
          <Button
            caution
            submit
            onClick={ () => onApply([{ id: BLE_READ_DUTY_CYCLE }]) }
          >Перезапросить</Button>
        </Row>
      </Footer>
    </div>
  }

  return <div className={ 'gas-and-oil-card' }>
    <Header
      title={ 'Интервал выхода на связь' }
      extra={ <Button
        flat
        icon={ 'sync' }
        style={ { marginRight: 'calc(var(--iq-grid-spacing) * -0.5)' } }
        onClick={ () => onApply([{ id: BLE_READ_DUTY_CYCLE }]) }
      /> }
    />

    <div className={ 'content' }>
      <Row>
        <Input
          size={ 12 }
          name={ 'dutyCycle' }
          type={ 'number' }
          placeholder={ 'Значение в секундах' }
          value={ value }
          onChange={ setValue }
          hintVisible
          checkValidity={ checkValidity }
          hint={ `0 c .. 65 535 c` }
        />
      </Row>
    </div>

    <Footer>
      <Row justify={ 'end' }>
        <Button
          caution
          submit
          onClick={ handleApplyClick }
        >Записать на устройство</Button>
      </Row>
    </Footer>
  </div>
}

function BleColdStartCard(props) {
  const {
    data: {
      coldStart,
      errorCode,
    },
    onApply,
  } = props

  const [value, setValue] = useState(coldStart)

  const handleApplyClick = useCallback(() => {
    const buffer = new ArrayBuffer(3)
    const view = new DataView(buffer)

    view.setUint8(0, BLE_SET_COLD_START)
    view.setUint16(1, value, false)

    return onApply([
      { id: BLE_SET_COLD_START, data: new Uint8Array(buffer) },
    ])
  }, [value])

  const checkValidity = useCallback((value) => {
    return value >= 0 && value <= 65535
  }, [])

  useEffect(() => {
    setValue(coldStart)
  }, [coldStart])

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error' }>
      <Header title={ 'Время прогрева датчика' }/>

      <div className={ 'content' }>
        <b>Ошибка при получении данных ({ errorCode })</b>
      </div>

      <Footer>
        <Row justify={ 'end' }>
          <Button
            caution
            submit
            onClick={ () => onApply([{ id: BLE_READ_COLD_START }]) }
          >Перезапросить</Button>
        </Row>
      </Footer>
    </div>
  }

  return <div className={ 'gas-and-oil-card' }>
    <Header
      title={ 'Время прогрева датчика' }
      extra={ <Button
        flat
        icon={ 'sync' }
        style={ { marginRight: 'calc(var(--iq-grid-spacing) * -0.5)' } }
        onClick={ () => onApply([{ id: BLE_READ_COLD_START }]) }
      /> }
    />

    <div className={ 'content' }>
      <Row>
        <Input
          size={ 12 }
          name={ 'dutyCycle' }
          type={ 'number' }
          placeholder={ 'Значение в миллисекундах' }
          value={ value }
          onChange={ setValue }
          hintVisible
          checkValidity={ checkValidity }
          hint={ `0 мc .. 65 535 мc` }
        />
      </Row>
    </div>

    <Footer>
      <Row justify={ 'end' }>
        <Button
          caution
          submit
          onClick={ handleApplyClick }
        >Записать на устройство</Button>
      </Row>
    </Footer>
  </div>
}

function BleLoRaWANFrameCard(props) {
  const {
    data: {
      voltage,
      temperature,
      value,
      errorCode,
    },
    onApply,
  } = props

  if (errorCode) {
    return <div className={ 'gas-and-oil-card error' }>
      <Header title={ 'LoRaWAN кадр - Токовая петля' }/>

      <div className={ 'content' }>
        <b>Ошибка при получении данных ({ errorCode })</b>
      </div>

      <Footer>
        <Row justify={ 'end' }>
          <Button
            caution
            submit
            onClick={ () => onApply([{ id: BLE_FRAME }]) }
          >Перезапросить</Button>
        </Row>
      </Footer>
    </div>
  }

  return <div className={ 'gas-and-oil-card' }>
    <Header
      title={ 'LoRaWAN кадр - Токовая петля' }
      extra={ <Button
        flat
        icon={ 'sync' }
        style={ { marginRight: 'calc(var(--iq-grid-spacing) * -0.5)' } }
        onClick={ () => onApply([{ id: BLE_FRAME }]) }
      /> }
    />

    <div className={ 'content' }>
      <div>
        <span>Напряжение батареи: </span>
        <b>{ voltage } мВ</b>
      </div>
      <div>
        <span>Температура: </span>
        <b>{ temperature } °C</b>
      </div>
      <div>
        <span>Токовый вход (4-20mA): </span>
        <b>{ value.toFixed(2) } мА</b>
      </div>
    </div>
  </div>
}

function BleCard({ data, onApply }) {
  useEffect(() => {
    console.log('BLE DATA CHANGED', data)
  }, [data])

  const card = useMemo(() => {
    switch (data.id) {
      case BLE_READ_STATE:
        return <BleCurrentValueCard
          data={ data }
          onApply={ onApply }
        />
      case BLE_READ_UNIT:
        return <BleUnitCard
          data={ data }
          onApply={ onApply }
        />
      case BLE_READ_COLD_START:
        return <BleColdStartCard
          data={ data }
          onApply={ onApply }
        />
      case BLE_READ_DUTY_CYCLE:
        return <BleDutyCycleCard
          data={ data }
          onApply={ onApply }
        />
      case BLE_READ_UPLINKS_COUNT:
        return <BleBootCountCard
          data={ data }
          onApply={ onApply }
        />
      case BLE_FRAME:
        return <BleLoRaWANFrameCard
          data={ data }
          onApply={ onApply }
        />
    }
  }, [data])

  return card || <p><Status accent>Card: { data.id.toString(16) } { JSON.stringify(data) }</Status></p>
}

function TunerView() {
  const { init, connect, write, subscribe, device, error, disconnect } = useBLE()
  const queueFns = useRef({ write: async () => {} })
  const [pending, setPending] = useState(false)
  const [openFlashModal, setOpenFlashModal] = useState(false)
  const [binaryFile, setBinaryFile] = useState(null)
  const [progress, setProgress] = useState(0)
  const [cards, setCards] = useState({})
  const cmdAckRef = useRef(0)
  const fwAckRef = useRef(0)
  const expectedSectorIndexRef = useRef(0)

  const crc16 = useCallback((init, data, len) => {
    let crc = init
    for (let i = 0; i < len; i++) {
      crc ^= data[i] << 8
      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = ((crc << 1) ^ 0x1021) & 0xffff
        } else {
          crc <<= 1
        }
      }
    }
    return crc & 0xffff
  }, [])

  const parseFirmwareNotification = useCallback((dv) => {
    if (!dv?.buffer || dv.buffer.byteLength < 20) return

    const recvCrc = dv.getUint16(18, true)
    const computed = crc16(0, new Uint8Array(dv.buffer, 0, 16), 18)

    if (computed !== recvCrc) return

    const sectorIndex = dv.getUint16(0, true)
    const fwAns = dv.getUint16(2, true)

    if (fwAns === 0 && expectedSectorIndexRef.current === sectorIndex) {
      fwAckRef.current = 1
    } else {
      fwAckRef.current = 0
    }
  }, [crc16])

  const parseCommandNotification = useCallback((dv) => {
    if (!dv?.buffer || dv.buffer.byteLength < 20) return

    const recvCrc = dv.getUint16(18, true)
    const computed = crc16(0, new Uint8Array(dv.buffer, 0, 16), 18)

    if (computed !== recvCrc) return

    if (dv.getUint16(0, true) === 3) {
      const cmdType = dv.getUint16(2, true)
      if (cmdType === 1 || cmdType === 2 || cmdType === 4) {
        const ans = dv.getUint16(4, true)
        cmdAckRef.current = ans === 0 ? 1 : 0
      }
    }
  }, [crc16])

  const waitForCommandAck = useCallback((timeoutMs) => {
    return new Promise((resolve) => {
      if (cmdAckRef.current === 1) return resolve(true)
      let intervalId = null

      const check = () => {
        if (cmdAckRef.current === 1) {
          if (intervalId) clearInterval(intervalId)
          resolve(true)
        }
      }

      intervalId = setInterval(check, RESPONSE_POLL_INTERVAL_MS)
      setTimeout(() => {
        if (intervalId) clearInterval(intervalId)
        resolve(false)
      }, timeoutMs)
    })
  }, [])

  const waitForFirmwareAck = useCallback((timeoutMs) => {
    return new Promise((resolve) => {
      if (fwAckRef.current === 1) return resolve(true)
      let intervalId = null

      const check = () => {
        if (fwAckRef.current === 1) {
          if (intervalId) clearInterval(intervalId)
          resolve(true)
        }
      }

      intervalId = setInterval(check, RESPONSE_POLL_INTERVAL_MS)
      setTimeout(() => {
        if (intervalId) clearInterval(intervalId)
        resolve(false)
      }, timeoutMs)
    })
  }, [])

  const buildStartCommand = useCallback((type, fileSize) => {
    const bytes = new Uint8Array(20)
    const view = new DataView(bytes.buffer)
    view.setUint16(0, BLE_OTA_START, true)
    view.setUint32(2, fileSize, true)
    const crc = crc16(0, bytes, 18)
    view.setUint16(18, crc, true)
    return bytes
  }, [crc16])

  const buildStopCommand = useCallback(() => {
    const bytes = new Uint8Array(20)
    const view = new DataView(bytes.buffer)
    view.setUint16(0, BLE_OTA_STOP, true)
    const crc = crc16(0, bytes, 18)
    view.setUint16(18, crc, true)
    return bytes
  }, [crc16])

  const extractSector = useCallback((file, offset) => {
    const end = Math.min(offset + SECTOR_SIZE, file.byteLength)
    return file.slice(offset, end)
  }, [])

  const createHeader = useCallback((sectorIndex, sequence, isLast) => {
    return new Uint8Array([
      sectorIndex & LAST_SEQUENCE,
      (sectorIndex >> 8) & LAST_SEQUENCE,
      isLast ? LAST_SEQUENCE : sequence,
    ])
  }, [])

  const appendCrc = useCallback((packet, crc) => {
    const withCrc = new Uint8Array(packet.length + 2)
    withCrc.set(packet, 0)
    withCrc[packet.length] = crc & LAST_SEQUENCE
    withCrc[packet.length + 1] = (crc >> 8) & LAST_SEQUENCE
    return withCrc
  }, [])

  const buildPacket = useCallback(({ sectorIndex, sequence, isLast, payload, crc }) => {
    const header = createHeader(sectorIndex, sequence, isLast)
    const packet = new Uint8Array(PACKET_HEADER_SIZE + payload.byteLength)

    packet.set(header, 0)
    packet.set(new Uint8Array(payload), PACKET_HEADER_SIZE)

    return isLast ? appendCrc(packet, crc) : packet
  }, [createHeader, appendCrc])

  const preparePacket = useCallback(({ sector, sectorOffset, sequence, sectorIndex, packetSize, crc }) => {
    const maxPayloadSize = packetSize - PACKET_HEADER_SIZE
    const remainingInSector = sector.byteLength - sectorOffset
    const payloadSize = Math.min(maxPayloadSize, remainingInSector)

    const isLast = sectorOffset + payloadSize >= sector.byteLength
    const payload = sector.slice(sectorOffset, sectorOffset + payloadSize)

    const updatedCrc = crc16(
      crc,
      new Uint8Array(payload),
      payload.byteLength,
    )

    const packet = buildPacket({
      sectorIndex,
      sequence,
      isLast,
      payload,
      crc: updatedCrc,
    })

    return {
      packet,
      payloadSize,
      isLast,
      crc: updatedCrc,
    }
  }, [crc16, buildPacket])

  const updateProgress = useCallback((written, total) => {
    const percentage = Math.round((100 * written) / total)
    setProgress(percentage)
  }, [])

  const handleLastPacket = useCallback(async (state) => {
    const ackReceived = await waitForFirmwareAck(FW_TIMEOUT_MS)

    if (!ackReceived && state.written < state.totalBytes) {
      errorMessage('Firmware ACK timeout')
      return false
    }

    updateProgress(state.written, state.totalBytes)
    return true
  }, [waitForFirmwareAck, updateProgress])

  const sendPacket = useCallback(async (packet, deviceId, sectorIndex, isLast, state) => {
    fwAckRef.current = 0
    expectedSectorIndexRef.current = sectorIndex

    await BleClient.writeWithoutResponse(
      deviceId,
      numberToUUID(PTA_SERVICE_UUID),
      numberToUUID(BLE_OTA_RECV_FW_UUID),
      numbersToDataView(Array.from(packet)),
    )

    if (!isLast) return true

    return await handleLastPacket(state)
  }, [handleLastPacket])

  const processSector = useCallback(async (state, packetSize, file, deviceId) => {
    const sector = extractSector(file, state.written)

    if (sector.byteLength === 0) return true

    let sectorOffset = 0
    let sequence = 0
    let crc = 0

    while (sectorOffset < sector.byteLength) {
      const packetInfo = preparePacket({
        sector,
        sectorOffset,
        sequence,
        sectorIndex: state.sectorIndex,
        packetSize,
        crc,
      })

      state.written += packetInfo.payloadSize
      crc = packetInfo.crc

      const success = await sendPacket(
        packetInfo.packet,
        deviceId,
        state.sectorIndex,
        packetInfo.isLast,
        state,
      )

      if (!success) return false

      sectorOffset += packetInfo.payloadSize
      sequence++
    }

    return true
  }, [extractSector, preparePacket, sendPacket])

  const sendFirmware = useCallback(async (packetSize, file, deviceId) => {
    const state = {
      sectorIndex: 0,
      written: 0,
      totalBytes: file.byteLength,
    }

    try {
      while (state.written < state.totalBytes) {
        const success = await processSector(state, packetSize, file, deviceId)
        if (!success) return false

        state.sectorIndex++
      }
      return true
    } catch (error) {
      errorMessage(`Firmware upload failed: ${ error.message }`)
      return false
    }
  }, [processSector])

  const handleFlashSubmit = useCallback(async () => {
    if (!device.connected || !binaryFile) return

    try {
      // Subscribe notifications
      await BleClient.startNotifications(device.id, numberToUUID(PTA_SERVICE_UUID), numberToUUID(BLE_OTA_RECV_FW_UUID), (dv) => {
        try { parseFirmwareNotification(dv) } catch {}
      })
      await BleClient.startNotifications(device.id, numberToUUID(PTA_SERVICE_UUID), numberToUUID(BLE_OTA_CMD_UUI), (dv) => {
        try { parseCommandNotification(dv) } catch {}
      })

      // Send START
      const startCmd = buildStartCommand('app', binaryFile.byteLength)
      cmdAckRef.current = 0
      await write(PTA_SERVICE_UUID, BLE_OTA_CMD_UUI, Array.prototype.slice.call(startCmd))
      const cmdAck = await waitForCommandAck(COMMAND_TIMEOUT_MS)

      if (!cmdAck) {
        errorMessage('START timeout')
        return
      }

      // Stream firmware
      const ok = await sendFirmware(DEFAULT_PACKET_SIZE, binaryFile, device.id)
      if (!ok) {
        errorMessage('FW timeout')
        return
      }

      // Send STOP (if still connected). Some devices reboot immediately after flashing.
      try {
        if (device.connected) {
          const stopCmd = buildStopCommand()
          cmdAckRef.current = 0
          await write(PTA_SERVICE_UUID, BLE_OTA_CMD_UUI, Array.prototype.slice.call(stopCmd))
          const stopAck = await waitForCommandAck(COMMAND_TIMEOUT_MS)
          if (!stopAck) {
            // If device rebooted and we lost STOP ACK, still treat as success since image fully written
            console.warn('STOP timeout, proceeding because image written')
          }
        }
      } catch (e) {
        console.warn('STOP write failed, likely device rebooted, proceeding', e)
      }
      Message({
        title: 'Устройства успешно прошит',
        type: 'success',
        timeout: 3000,
      })
    } catch (e) {
      errorMessage(e.message || 'OTA Error')
    }
  }, [device, binaryFile, write, buildStartCommand, buildStopCommand, parseCommandNotification, parseFirmwareNotification, sendFirmware, waitForCommandAck])

  const handleFileChange = useCallback((input) => {
    const fileCandidate = input[0]

    if (!fileCandidate) {
      setBinaryFile(null)
      return
    }

    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setBinaryFile(reader.result)
    })

    reader.readAsArrayBuffer(fileCandidate)
  }, [])

  const queue = useMemo(() => {
    let lastNext = noop
    let lastTask

    const q = new Queue((task, next) => {
      lastTask = task
      lastNext = next

      queueFns.current.write(AMBER_TUNER_SERVICE, AMBER_TUNER_CHARACTERISTIC, task.data || [task.id])
        .catch(() => q.retry(task))
    }, {
      maxRetries: 5,
      retryDelay: 300,
      afterProcessDelay: 300,
      maxTimeout: 30000,
    })

    q.on('response', (response) => {
      if (lastTask.id !== response.id) return q.retry(lastTask)

      if (lastTask.id < 0x50) {
        setCards(assoc(response.id, response))
      } else {
        setCards((cards) => {
          const values = cards[lastTask.id - 0x20]

          return {
            ...cards,
            [values.id]: {
              ...values,
              ...response,
              id: values.id,
            },
          }
        })
      }

      if (response.errorCode) {
        q.retry(lastTask)
      } else {
        lastNext()
      }
    })

    q.on('start-working', () => setPending(true))
    q.on('idle', () => setPending(false))
    return q
  }, [])

  const cardItems = useMemo(() => {
    return Object.values(cards).sort(({ id: a }, { id: b }) => {
      if (a === BLE_READ_UNIT) return 100
      if (b === BLE_READ_UNIT) return -100
      return a - b
    })
  }, [cards])

  const handleScan = useCallback(async () => {
    await connect({
      // namePrefix: 'Amber',
      services: [AMBER_SERVICE],
      optionalServices: [
        BLE_INFORMATION_SERVICE,
        AMBER_TUNER_SERVICE,
        PTA_SERVICE_UUID,
      ],
    })
  }, [connect])

  const handleDataApply = useCallback(async (data) => {
    data.map((task) => queue.push(task))
  }, [queue])

  useEffect(() => {
    init().catch(console.error)
  }, [])

  useEffect(() => {
    queueFns.current.write = write
  }, [write])

  useEffect(() => {
    if (!device.connected) {
      setCards({})
      setProgress(0)
      return
    }

    setProgress(0)

    const unsubscribe = subscribe(
      AMBER_TUNER_SERVICE,
      AMBER_TUNER_CHARACTERISTIC,
      (response) => queue.emit('response', response),
    )

    queue.push({ id: BLE_READ_STATE })
    queue.push({ id: BLE_READ_COLD_START })
    queue.push({ id: BLE_READ_DUTY_CYCLE })
    queue.push({ id: BLE_READ_UPLINKS_COUNT })
    queue.push({ id: BLE_READ_UNIT })
    queue.push({ id: BLE_FRAME })

    return () => {
      unsubscribe()
      disconnect().catch(noop)
    }
  }, [queue, disconnect, device])

  return <>
    <Notification
      status={ 'info' }
      visible={ !!progress && device.connected }
    >
      <div style={ {
        width: `${ progress }%`,
        height: '2rem',
        background: 'var(--iq-color)',
        borderRadius: 'var(--iq-input-border-radius)',
        transition: '0.4s ease',
      } }
      />
    </Notification>

    <Modal
      title={ 'Прошить файл' }
      isOpened={ openFlashModal }
      onClose={ () => setOpenFlashModal(false) }
      onApply={ () => { handleFlashSubmit() } }
      footerActions={ [{
        key: 'flash',
        primary: true,
        title: 'Применить',
        submit: true,
      }, {
        key: 'cancel',
        danger: true,
        title: 'Закрыть',
      }] }
    >
      <Input
        type={ 'file' }
        name={ 'binary' }
        onChange={ handleFileChange }
      />
    </Modal>

    <Row breakline={ 'tablet' } align={ 'stretch' }>
      <Col size={ 10 }>
        <h4>Устройство: <b>{ device.name || 'N/A' }</b></h4>
        <div>DevEUI: <b>{ device.sn || 'N/A' }</b></div>
        <div>Модель: <b>{ device.model || 'N/A' }</b></div>
        <div>Версия платы: <b>{ device.hwVersion || 'N/A' }</b></div>
        <div>Версия прошивки: <b>{ device.fwVersion || 'N/A' }</b></div>
      </Col>

      <Row
        style={ {
          flexDirection: 'column',
        } }
        breakline={ 'tablet' }
        align={ 'end' }
      >
        <Col size={ 6 }>
          { device.connected && <Button
            onClick={ () => setOpenFlashModal(true) }
            secondary
            icon={ 'developer_board' }
            size={ 2 }
            style={ { margin: 0, height: '100%' } }
          /> }
        </Col>

        <Col size={ 6 }>
          <Button
            primary={ !device.connected }
            danger={ device.connected }
            size={ 2 }
            onClick={ device.connected ? disconnect : handleScan }
            icon={ 'bluetooth' }
            style={ { margin: 0, height: '100%' } }
          />
        </Col>
      </Row>
    </Row>

    <p/>

    { !!error && <>
      <Status error style={ { textAlign: 'left' } }>{ error.message }</Status>
      <p/>
    </> }

    <div className={ cn('production-cards-wrapper', { loading: pending }) }>
      { cardItems.map((data) => {
        return <BleCard
          key={ data.id }
          data={ data }
          onApply={ handleDataApply }
        />
      }) }
    </div>
  </>
}

export default function TunerPage() {
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.logs'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(true)
    toggleScrollbars(true)
  }, [translatedTitle])

  return <Page
    component={ TunerView }
    errorView={ ErrorView }
  />
}
